<script setup lang="ts">
import { onMounted, onUnmounted, shallowRef } from 'vue'
import { useRouter, type RouteLocationRaw } from 'vue-router'
import { useAuthStore } from '@/@vxapp/Auth/useAuthStore'
import { createAuthChannel } from '@/@vxapp/Auth/tabChannel'
import { AuthErrors } from '@/@vxapp/Auth/AuthErrors'
import { classifySessionEnd } from '@/@vxapp/Auth/sessionEnded'
import { StorageManager, StorageKey } from '@/@vxapp/@core/AppStorage/StorageManager'
import { PageMeta } from '@/@vxapp/@Enums/PageMeta'
import { createLogger } from '@/@vxapp/@core/Logger/VxLog'
import { authReturnRouteOwner, resolveAuthReturnRoute } from '@/plugins/2.router/authReturnRoute'

definePage({ meta: { ...PageMeta.Auth.waiting.meta, pageTitle: PageMeta.Auth.waiting.title } })

const router = useRouter()
const authStore = useAuthStore()
const log = createLogger('AuthWaiting')
const returnRoute = shallowRef<unknown>(null)
const showButtons = shallowRef(false)
const message = shallowRef<string | null>(null)
const hasNavigationStarted = shallowRef(false)
const hasTerminalRenewalFailure = shallowRef(false)
const timerId = shallowRef<ReturnType<typeof setTimeout> | null>(null)
let stopPolling: ReturnType<typeof setInterval> | null = null
let channel: ReturnType<typeof createAuthChannel> = null
let isRenewalCheckInProgress = false
let isAuthFlowActive = true

const fallbackRoute = { name: PageMeta.Dashboard.ecommerce.name } as RouteLocationRaw

function stopRenewalListeners() {
  if (stopPolling) {
    clearInterval(stopPolling)
    stopPolling = null
  }

  channel?.close?.()
  channel = null
}

async function pushRoute(location: RouteLocationRaw): Promise<boolean> {
  try {
    const navigationFailure = await router.push(location)

    return !navigationFailure
  } catch (error) {
    log.error('Unable to navigate after token renewal', error)

    return false
  }
}

async function restoreReturnRoute() {
  if (!isAuthFlowActive || hasNavigationStarted.value || hasTerminalRenewalFailure.value) {
    return
  }

  hasNavigationStarted.value = true

  const savedLocation = resolveAuthReturnRoute(router, returnRoute.value, {
    owner: authReturnRouteOwner(authStore),
  })

  if (savedLocation && (await pushRoute(savedLocation))) {
    authStore.resetPageMeta()
    stopRenewalListeners()

    return
  }

  if (savedLocation) {
    log.warn('Saved return route navigation failed -> using dashboard fallback')
  } else {
    log.warn('Saved return route was missing or invalid -> using dashboard fallback')
  }

  if (await pushRoute(fallbackRoute)) {
    authStore.resetPageMeta()
    stopRenewalListeners()

    return
  }

  hasNavigationStarted.value = false
}

async function handleTerminalRenewalFailure(error: unknown) {
  if (!isAuthFlowActive) {
    return
  }

  log.error('Token renewal failed in the current tab', error)
  hasTerminalRenewalFailure.value = true
  stopRenewalListeners()

  const sessionEndReason = classifySessionEnd(error as Parameters<typeof classifySessionEnd>[0])

  if (sessionEndReason) {
    // The registered session-ended handler opens sign-in with the notice and saved route.
    await authStore.endSession(sessionEndReason)

    if (!isAuthFlowActive) {
      return
    }
  }

  // Keep the return route so signing in again can still restore the user's page.
  if (returnRoute.value) {
    authStore.setPageMeta(returnRoute.value, 'Auth Waiting (terminal renewal failure)', true)
  }

  message.value = 'Your session could not be restored. Sign in to continue.'
  showButtons.value = true
}

async function tryRefreshIfNeeded(rehydrateReason?: string) {
  if (
    !isAuthFlowActive ||
    hasTerminalRenewalFailure.value ||
    hasNavigationStarted.value ||
    isRenewalCheckInProgress
  ) {
    return
  }

  isRenewalCheckInProgress = true

  try {
    if (rehydrateReason) {
      await authStore.init(rehydrateReason, true)

      if (!isAuthFlowActive) {
        return
      }
    }

    const status = await authStore.getAuthStatus()

    if (!isAuthFlowActive) {
      return
    }

    if (status.isTokenExpired) {
      log.debug('Attempting token renewal')

      const renewalResult = await authStore.handleExpiredToken()

      if (!isAuthFlowActive) {
        return
      }

      if (renewalResult?.success) {
        await restoreReturnRoute()

        return
      }

      const isRenewingInAnotherTab = renewalResult?.error === AuthErrors.TOKEN_ALREADY_REFRESHING

      if (!isRenewingInAnotherTab && !StorageManager().isLockActive(StorageKey.RefreshInProg)) {
        await handleTerminalRenewalFailure(renewalResult?.error)
      }
    } else {
      await restoreReturnRoute()
    }
  } finally {
    isRenewalCheckInProgress = false
  }
}

async function onProceed() {
  const status = await authStore.getAuthStatus()

  if (!isAuthFlowActive) {
    return
  }

  if (status.isTokenExpired) {
    message.value = 'Still refreshing… please wait'
    await tryRefreshIfNeeded()

    return
  }
  await restoreReturnRoute()
}

async function onLogout() {
  const preservedReturnRoute = hasTerminalRenewalFailure.value ? returnRoute.value : null

  isAuthFlowActive = false
  stopRenewalListeners()

  await authStore.logout(router)

  // "Sign in again" after a failed renewal keeps the page to return to; "Logout" does not.
  if (preservedReturnRoute) {
    authStore.setPageMeta(preservedReturnRoute, 'Auth Waiting (sign in again)', true)
  }
}

function startShowButtonsTimer(durationMs = 10000) {
  return setTimeout(() => {
    showButtons.value = true
  }, durationMs)
}

onMounted(async () => {
  log.debug('Welcome to the waiting room')
  returnRoute.value = authStore.pageMeta
  timerId.value = startShowButtonsTimer()

  const authStatus = await authStore.getAuthStatus()

  if (authStatus.isAuthenticated && !authStatus.isTokenExpired) {
    log.debug('Token has not expired -> restoring the return route')
    await restoreReturnRoute()
  } else {
    channel = createAuthChannel(async m => {
      if (m?.type === 'token-updated' && !hasTerminalRenewalFailure.value) {
        log.debug('Token update received -> refreshing persisted auth state')
        await tryRefreshIfNeeded('Auth Waiting (token-updated)')
      }
    })

    const POLL_MS = 500

    stopPolling = setInterval(async () => {
      const lockActive = StorageManager().isLockActive(StorageKey.RefreshInProg)

      if (!lockActive) {
        await tryRefreshIfNeeded('Auth Waiting (lock polling)')
      }
    }, POLL_MS)

    await tryRefreshIfNeeded()
  }
})

onUnmounted(() => {
  isAuthFlowActive = false
  if (timerId.value) clearTimeout(timerId.value)
  stopRenewalListeners()
})
</script>

<template>
  <div class="reauth-wrapper">
    <div class="reauth-content text-center">
      <VProgressCircular
        v-if="!hasTerminalRenewalFailure"
        indeterminate
        size="64"
        width="4"
        color="primary"
        class="mb-6"
      />

      <h4 class="reauth-title mb-2">
        {{ hasTerminalRenewalFailure ? 'Sign in again' : "one mo'" }}
      </h4>
      <p
        v-if="!hasTerminalRenewalFailure"
        class="reauth-subtitle mb-8"
      >
        we're just re-authenticating you
      </p>

      <p
        v-if="message"
        class="reauth-subtitle mb-8"
      >
        {{ message }}
      </p>

      <div
        v-if="showButtons"
        class="reauth-actions"
      >
        <VBtn
          v-if="!hasTerminalRenewalFailure"
          block
          color="primary"
          class="mb-4"
          @click="onProceed"
        >
          I've waited long enough
        </VBtn>
        <VBtn
          block
          outlined
          color="primary"
          @click="onLogout"
        >
          {{ hasTerminalRenewalFailure ? 'Sign in again' : 'Logout' }}
        </VBtn>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.reauth-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 2rem;
}

.reauth-content {
  max-width: 360px;
  width: 100%;
}

.reauth-title {
  font-size: 1.75rem;
  font-weight: 500;
}

.reauth-actions {
  display: flex;
  flex-direction: column;
  margin-bottom: 2rem;

  .v-btn {
    font-weight: 500;
  }
}

.reauth-illustration {
  margin-bottom: 2rem;

  img {
    max-width: 100%;
  }
}

.reauth-footer-mask {
  position: absolute;
  bottom: 0;
  width: 100%;
}
</style>
